import * as React from "react";

import {
  RobotoMarkdownLink,
  type RobotoMarkdownLinkProps,
} from "@/shared/components/RobotoMarkdown/RobotoMarkdownLink";

import {
  isAIInlineTimestampHighlightedLink,
  isAIInlineTimestampLink,
  parseAIInlineTimestampNs,
} from "./aiInlineTimestampLinks";
import { InlineTimestamp } from "./InlineTimestamp";

/**
 * Link renderer for AI thread markdown. Handles the thread-only `ai-ts:` /
 * `ai-ts-hl:` synthetic URLs (emitted by remarkTimestampMentions) up
 * front, then defers everything else — `timestamp:`, domain links,
 * plain external links — to the shared {@link RobotoMarkdownLink}.
 */
export const AgentThreadMarkdownLink: React.FC<
  React.PropsWithChildren<RobotoMarkdownLinkProps>
> = ({ href, children, ...rest }) => {
  if (href !== undefined && isAIInlineTimestampLink(href)) {
    const ns = parseAIInlineTimestampNs(href);
    if (ns !== undefined) {
      return (
        <InlineTimestamp
          ns={ns}
          highlighted={isAIInlineTimestampHighlightedLink(href)}
        />
      );
    }
    // Fail closed: an `ai-ts:` / `ai-ts-hl:` link with an unparseable
    // payload signals an upstream plugin defect. Render the original
    // label as plain text rather than emitting a broken `<a>`, so the
    // bug doesn't hide behind innocuous-looking output.
    return <>{children}</>;
  }

  return (
    <RobotoMarkdownLink href={href} {...rest}>
      {children}
    </RobotoMarkdownLink>
  );
};
